import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays, Plus, Save, Trash2, X, Lock, ShieldAlert, Upload, ArrowUp, ArrowDown,
  Image as ImageIcon, Send, Repeat, UserPlus, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  fetchMyDjApplication, listMyDjEvents, createDjEvent, updateDjEvent, publishDjEvent,
  deleteDjEvent, updateDjSeries, deleteDjSeries, uploadEventPosterStaged, fetchEventLineup,
  fetchApprovedDjs, validatePosterFile, posterUrl, US_STATES,
} from '@/lib/featuresApi';

const DELETE_WARNING = 'This will permanently remove the event for everyone, including users who saved it.';
const WEEKDAYS = [['MO', 'Mon'], ['TU', 'Tue'], ['WE', 'Wed'], ['TH', 'Thu'], ['FR', 'Fri'], ['SA', 'Sat'], ['SU', 'Sun']];

const EMPTY = {
  title: '', description: '', category: '', venue: '', address: '', city: '', state: '', zip: '',
  start_date: '', start_time: '', end_date: '', end_time: '',
  event_type: 'free', ticket_link: '', ticket_price: '',
  performing_self: false,
};

const EMPTY_REC = { freq: 'none', interval: 1, byday: [], bymonthday: '', endType: 'count', endCount: 8, endDate: '' };

const statusBadge = (s) => {
  if (s === 'published') return <Badge className="bg-green-600 capitalize">{s}</Badge>;
  if (s === 'archived') return <Badge variant="outline" className="capitalize">{s}</Badge>;
  return <Badge variant="secondary" className="capitalize">{s || 'draft'}</Badge>;
};

// A DJ-stored datetime is date + HH:MM. Past = end (or start) before now.
function isPast(item) {
  const date = item.end_date || item.start_date;
  const time = item.end_time || item.start_time || '23:59';
  if (!date) return false;
  const dt = new Date(`${String(date).slice(0, 10)}T${String(time).slice(0, 5)}:00Z`);
  return !isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

export default function DJEvents() {
  usePageMeta('My Events', 'Create and manage your events on SoundWolves.');
  const [authed] = useState(api.isAuthenticated());
  const [approved, setApproved] = useState(null);     // null=loading, true/false
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('upcoming');
  const [approvedDjs, setApprovedDjs] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('create');         // create | edit-event | edit-series | edit-occurrence
  const [editId, setEditId] = useState(null);
  const [occDate, setOccDate] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [rec, setRec] = useState(EMPTY_REC);
  const [posters, setPosters] = useState([]);          // [{key,url}]
  const [perfDjIds, setPerfDjIds] = useState([]);
  const [perfGuests, setPerfGuests] = useState([]);
  const [guestInput, setGuestInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    const list = await listMyDjEvents().catch(() => []);
    setItems(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!api.isAuthenticated()) { if (active) { setApproved(false); setLoading(false); } return; }
      try {
        const app = await fetchMyDjApplication();
        if (!active) return;
        const ok = app && app.status === 'approved';
        setApproved(ok);
        if (ok) {
          await refresh();
          fetchApprovedDjs().then((d) => active && setApprovedDjs(Array.isArray(d) ? d : [])).catch(() => {});
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [refresh]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setRec((r) => ({ ...r, [k]: v }));

  const resetForm = () => {
    setForm(EMPTY); setRec(EMPTY_REC); setPosters([]); setPerfDjIds([]); setPerfGuests([]);
    setGuestInput(''); setEditId(null); setOccDate(null);
  };

  const openCreate = () => { resetForm(); setMode('create'); setShowForm(true); };

  const fillFromItem = (item) => ({
    title: item.title || '', description: item.description || '', category: item.category || '',
    venue: item.venue || '', address: item.address || '', city: item.city || '',
    state: item.state || '', zip: item.zip || '',
    start_date: (item.start_date || '').slice(0, 10), start_time: (item.start_time || '').slice(0, 5),
    end_date: (item.end_date || '').slice(0, 10), end_time: (item.end_time || '').slice(0, 5),
    event_type: item.event_type || 'free', ticket_link: item.ticket_link || '',
    ticket_price: item.ticket_price ?? '', performing_self: !!item.performing_self,
  });

  const loadPerformers = async (eventId, selfDjId) => {
    try {
      const lu = await fetchEventLineup(eventId);
      const ids = (lu?.approved_djs || []).map((d) => d.id).filter((id) => id && id !== selfDjId);
      setPerfDjIds(ids);
      setPerfGuests(lu?.guest_djs || []);
    } catch (_) { /* no lineup yet */ }
  };

  const openEditEvent = async (item) => {
    resetForm();
    setMode('edit-event'); setEditId(item.id);
    setForm(fillFromItem(item));
    setPosters((item.posters || []).map((k) => ({ key: k, url: posterUrl(k) })));
    await loadPerformers(item.id, item.creator_dj_id);
    setShowForm(true);
  };

  const openEditSeries = (item) => {
    resetForm();
    setMode('edit-series'); setEditId(item.series_id || item.id);
    setForm(fillFromItem(item));
    setPosters((item.posters || []).map((k) => ({ key: k, url: posterUrl(k) })));
    setPerfDjIds((item.performer_dj_ids || []).filter((id) => id !== item.creator_dj_id));
    setPerfGuests(item.performer_guest_names || []);
    setShowForm(true);
  };

  const openEditOccurrence = (seriesItem, date) => {
    resetForm();
    setMode('edit-occurrence'); setEditId(seriesItem.series_id || seriesItem.id); setOccDate(date);
    const ov = (seriesItem.overrides || {})[date] || {};
    setForm({ ...fillFromItem({ ...seriesItem, ...ov }), start_date: date });
    setPosters(((ov.posters || seriesItem.posters) || []).map((k) => ({ key: k, url: posterUrl(k) })));
    setShowForm(true);
  };

  // ----- posters -----
  const onPickPoster = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (posters.length >= 3) { toast.error('You can add at most 3 posters.'); return; }
    const err = validatePosterFile(file);
    if (err) { toast.error(err); return; }
    setUploading(true);
    try {
      const res = await uploadEventPosterStaged(file);
      setPosters((p) => [...p, { key: res.poster_key, url: res.url || posterUrl(res.poster_key) }]);
    } catch (e2) {
      toast.error(e2.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  const movePoster = (i, dir) => setPosters((p) => {
    const n = [...p]; const j = i + dir;
    if (j < 0 || j >= n.length) return p;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const removePoster = (i) => setPosters((p) => p.filter((_, idx) => idx !== i));

  // ----- performers -----
  const addGuest = () => {
    const g = guestInput.trim();
    if (!g) return;
    if (!perfGuests.some((x) => x.toLowerCase() === g.toLowerCase())) setPerfGuests((l) => [...l, g]);
    setGuestInput('');
  };
  const addDjId = (id) => { if (id && !perfDjIds.includes(id)) setPerfDjIds((l) => [...l, id]); };
  const djName = (id) => approvedDjs.find((d) => d.id === id)?.dj_name || id;

  const buildPayload = (status) => {
    const p = {
      title: form.title.trim(), description: form.description.trim() || null,
      category: form.category.trim(), venue: form.venue.trim(), address: form.address.trim(),
      city: form.city.trim(), state: form.state, zip: form.zip.trim(),
      start_date: form.start_date, start_time: form.start_time,
      end_date: form.end_date || null, end_time: form.end_time || null,
      event_type: form.event_type,
      ticket_link: form.event_type === 'paid' ? form.ticket_link.trim() : null,
      ticket_price: form.event_type === 'paid' && form.ticket_price !== '' ? Number(form.ticket_price) : null,
      posters: posters.map((p2) => p2.key),
      performing_self: !!form.performing_self,
      performer_dj_ids: perfDjIds,
      performer_guest_names: perfGuests,
    };
    if (status) p.status = status;
    return p;
  };

  const buildRecurrence = () => {
    if (mode !== 'create' || rec.freq === 'none') return undefined;
    const r = { freq: rec.freq, interval: Number(rec.interval) || 1 };
    if (rec.freq === 'weekly' || rec.freq === 'custom') r.byday = rec.byday;
    if (rec.freq === 'monthly' && rec.bymonthday) r.bymonthday = Number(rec.bymonthday);
    r.end = rec.endType === 'until'
      ? { type: 'until', date: rec.endDate }
      : { type: 'count', count: Number(rec.endCount) || 1 };
    return r;
  };

  const validateBeforePublish = () => {
    const req = ['title', 'category', 'venue', 'address', 'city', 'state', 'zip', 'start_date', 'start_time'];
    for (const k of req) if (!form[k]) return `Please fill in: ${k.replace('_', ' ')}`;
    if (posters.length < 1) return 'Add at least 1 poster before publishing.';
    if (form.event_type === 'paid') {
      if (!form.ticket_link.trim()) return 'Paid events need a ticket link.';
      if (form.ticket_price === '' || Number(form.ticket_price) <= 0) return 'Paid events need a price greater than 0.';
    }
    return null;
  };

  const submit = async (status) => {
    if (status === 'published') {
      const v = validateBeforePublish();
      if (v) { toast.error(v); return; }
    } else if (!form.title || !form.start_date || !form.start_time) {
      toast.error('Title, start date and start time are required.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'create') {
        const payload = buildPayload(status);
        const recurrence = buildRecurrence();
        if (recurrence) payload.recurrence = recurrence;
        await createDjEvent(payload);
        toast.success(recurrence ? 'Series created' : (status === 'published' ? 'Event published' : 'Draft saved'));
      } else if (mode === 'edit-event') {
        await updateDjEvent(editId, buildPayload(status));
        toast.success('Event updated');
      } else if (mode === 'edit-series') {
        await updateDjSeries(editId, buildPayload(status), { scope: 'series' });
        toast.success('Series updated');
      } else if (mode === 'edit-occurrence') {
        await updateDjSeries(editId, buildPayload(null), { scope: 'occurrence', occurrenceDate: occDate });
        toast.success('Occurrence updated');
      }
      setShowForm(false);
      resetForm();
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const publishExisting = async (item) => {
    try { await publishDjEvent(item.id); toast.success('Event published'); await refresh(); }
    catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const removeOneTime = async (item) => {
    if (!window.confirm(DELETE_WARNING)) return;
    try { await deleteDjEvent(item.id); toast.success('Event permanently deleted'); await refresh(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const removeSeries = async (item) => {
    if (!window.confirm(`Delete the entire series? ${DELETE_WARNING}`)) return;
    try { await deleteDjSeries(item.series_id || item.id, { scope: 'series' }); toast.success('Series permanently deleted'); await refresh(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const cancelOccurrence = async (item, date) => {
    if (!window.confirm(`Cancel the occurrence on ${date}? ${DELETE_WARNING}`)) return;
    try { await deleteDjSeries(item.series_id || item.id, { scope: 'occurrence', occurrenceDate: date }); toast.success('Occurrence cancelled'); await refresh(); }
    catch (e) { toast.error(e.message || 'Cancel failed'); }
  };

  // ---------- gates ----------
  if (!authed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <p className="mb-4">Please log in to manage your events.</p>
        <Link to="/login"><Button>Go to Login</Button></Link>
      </div>
    );
  }
  if (loading || approved === null) return <div className="max-w-3xl mx-auto px-4 py-16 text-center opacity-70">Loading…</div>;
  if (!approved) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <h1 className="text-xl font-semibold mb-2">Approved DJ access required</h1>
        <p className="opacity-80 mb-4">Only approved DJs can create events. Apply or check your status first.</p>
        <Link to="/dj-status"><Button>Check DJ Status</Button></Link>
      </div>
    );
  }

  const oneTime = items.filter((i) => !i.recurring);
  const series = items.filter((i) => i.recurring);
  const upcomingEvents = oneTime.filter((i) => !isPast(i));
  const pastEvents = oneTime.filter((i) => isPast(i));

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6" /> My Events</h1>
        {!showForm && <Button onClick={openCreate} data-testid="dj-create-event"><Plus className="h-4 w-4 mr-2" /> Create Event</Button>}
      </div>

      {showForm && (
        <EventForm
          mode={mode} form={form} set={set} rec={rec} setR={setR}
          posters={posters} onPickPoster={onPickPoster} movePoster={movePoster} removePoster={removePoster} uploading={uploading}
          approvedDjs={approvedDjs} perfDjIds={perfDjIds} addDjId={addDjId} setPerfDjIds={setPerfDjIds} djName={djName}
          perfGuests={perfGuests} guestInput={guestInput} setGuestInput={setGuestInput} addGuest={addGuest} setPerfGuests={setPerfGuests}
          busy={busy} occDate={occDate}
          onCancel={() => { setShowForm(false); resetForm(); }}
          onSaveDraft={() => submit('draft')} onPublish={() => submit('published')}
          onSaveOccurrence={() => submit(null)}
        />
      )}

      {!showForm && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-3">
            {series.map((s) => (
              <SeriesCard key={s.id} item={s} onEdit={() => openEditSeries(s)} onDelete={() => removeSeries(s)}
                onCancelOcc={(d) => cancelOccurrence(s, d)} onEditOcc={(d) => openEditOccurrence(s, d)} />
            ))}
            {upcomingEvents.length === 0 && series.length === 0 && <p className="opacity-60 text-center py-8">No upcoming events yet.</p>}
            {upcomingEvents.map((ev) => (
              <EventRow key={ev.id} ev={ev} past={false} onEdit={() => openEditEvent(ev)}
                onPublish={() => publishExisting(ev)} onDelete={() => removeOneTime(ev)} />
            ))}
          </TabsContent>

          <TabsContent value="past" className="space-y-3">
            {pastEvents.length === 0 && <p className="opacity-60 text-center py-8">No past events.</p>}
            {pastEvents.map((ev) => (
              <EventRow key={ev.id} ev={ev} past onEdit={null} onPublish={null} onDelete={null} />
            ))}
            <p className="text-xs opacity-60 flex items-center gap-1 pt-2"><Info className="h-3 w-3" /> Past events are read-only. Contact an admin for changes.</p>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function EventRow({ ev, past, onEdit, onPublish, onDelete }) {
  return (
    <Card>
      <CardContent className="py-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{ev.title || '(untitled)'}</span>
            {statusBadge(ev.status)}
            {past && <Badge variant="outline">Past</Badge>}
          </div>
          <div className="text-sm opacity-70 truncate">
            {[ev.city, ev.venue, [ev.start_date, ev.start_time].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'No details'}
          </div>
        </div>
        {!past && (
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
            {ev.status !== 'published' && <Button variant="outline" size="sm" onClick={onPublish}><Send className="h-3 w-3 mr-1" /> Publish</Button>}
            <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SeriesCard({ item, onEdit, onDelete, onCancelOcc, onEditOcc }) {
  const dates = item.occurrence_dates || [];
  return (
    <Card className="border-primary/30">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-primary" />
              <span className="font-semibold truncate">{item.title || '(untitled series)'}</span>
              {statusBadge(item.status)}
              <Badge variant="outline" className="capitalize">{item.recurrence?.freq || 'recurring'}</Badge>
            </div>
            <div className="text-sm opacity-70 truncate">
              {[item.city, item.venue].filter(Boolean).join(' · ')} · {dates.length} occurrence{dates.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onEdit}>Edit series</Button>
            <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        {dates.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {dates.slice(0, 16).map((d) => (
              <span key={d} className="inline-flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-xs">
                {d}
                <button type="button" className="opacity-60 hover:opacity-100" title="Edit this occurrence" onClick={() => onEditOcc(d)}>✎</button>
                <button type="button" className="opacity-60 hover:opacity-100 text-red-500" title="Cancel this occurrence" onClick={() => onCancelOcc(d)}>×</button>
              </span>
            ))}
            {dates.length > 16 && <span className="text-xs opacity-60">+{dates.length - 16} more</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EventForm(props) {
  const {
    mode, form, set, rec, setR, posters, onPickPoster, movePoster, removePoster, uploading,
    approvedDjs, perfDjIds, addDjId, setPerfDjIds, djName, perfGuests, guestInput, setGuestInput, addGuest, setPerfGuests,
    busy, occDate, onCancel, onSaveDraft, onPublish, onSaveOccurrence,
  } = props;
  const showRecurrence = mode === 'create';
  const isOccurrence = mode === 'edit-occurrence';
  const title = mode === 'create' ? 'Create event'
    : mode === 'edit-series' ? 'Edit series'
      : mode === 'edit-occurrence' ? `Edit occurrence (${occDate})` : 'Edit event';

  const toggleDay = (code) => setR('byday', rec.byday.includes(code) ? rec.byday.filter((c) => c !== code) : [...rec.byday, code]);

  return (
    <Card className="mb-6">
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-5">
          {/* Basics */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input id="title" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label htmlFor="cat">Category *</Label><Input id="cat" value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. House, Bollywood" /></div>
            <div><Label htmlFor="venue">Venue *</Label><Input id="venue" value={form.venue} onChange={(e) => set('venue', e.target.value)} /></div>
          </div>
          <div><Label htmlFor="desc">Description</Label><Textarea id="desc" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={2000} /></div>

          {/* Location (USA) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label htmlFor="addr">Full address *</Label><Input id="addr" value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
            <div><Label htmlFor="city">City *</Label><Input id="city" value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
            <div>
              <Label htmlFor="state">State *</Label>
              <select id="state" value={form.state} onChange={(e) => set('state', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><Label htmlFor="zip">ZIP *</Label><Input id="zip" value={form.zip} onChange={(e) => set('zip', e.target.value)} placeholder="12345" /></div>
          </div>

          {/* Date/time */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><Label htmlFor="sd">Start date *</Label><Input id="sd" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} disabled={isOccurrence} /></div>
            <div><Label htmlFor="st">Start time *</Label><Input id="st" type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} /></div>
            <div><Label htmlFor="ed">End date</Label><Input id="ed" type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} /></div>
            <div><Label htmlFor="et">End time</Label><Input id="et" type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} /></div>
          </div>

          {/* Pricing */}
          <div className="rounded-lg border border-border/60 p-4 space-y-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Event type:</span>
              <label className="flex items-center gap-1 text-sm"><input type="radio" name="etype" checked={form.event_type === 'free'} onChange={() => set('event_type', 'free')} /> Free</label>
              <label className="flex items-center gap-1 text-sm"><input type="radio" name="etype" checked={form.event_type === 'paid'} onChange={() => set('event_type', 'paid')} /> Paid</label>
            </div>
            {form.event_type === 'paid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tlink">Ticket / payment link *</Label>
                  <Input id="tlink" value={form.ticket_link} onChange={(e) => set('ticket_link', e.target.value)} placeholder="https://www.eventbrite.com/…" />
                  <p className="text-xs opacity-60 mt-1">Allowed: Eventbrite, Dice, Ticketmaster, PayPal, Stripe.</p>
                </div>
                <div><Label htmlFor="price">Starting price (USD) *</Label><Input id="price" type="number" min="0" step="0.01" value={form.ticket_price} onChange={(e) => set('ticket_price', e.target.value)} /></div>
              </div>
            )}
          </div>

          {/* Recurrence (create only) */}
          {showRecurrence && (
            <div className="rounded-lg border border-border/60 p-4 space-y-3">
              <div className="flex items-center gap-2"><Repeat className="h-4 w-4" /><span className="text-sm font-medium">Repeats</span></div>
              <select value={rec.freq} onChange={(e) => setR('freq', e.target.value)}
                className="flex h-10 w-full sm:w-56 rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="none">Does not repeat (one-time)</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom days</option>
              </select>
              {rec.freq !== 'none' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span>Every</span>
                    <Input type="number" min="1" max="52" value={rec.interval} onChange={(e) => setR('interval', e.target.value)} className="w-20" />
                    <span>{rec.freq === 'daily' ? 'day(s)' : rec.freq === 'monthly' ? 'month(s)' : 'week(s)'}</span>
                  </div>
                  {(rec.freq === 'weekly' || rec.freq === 'custom') && (
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map(([code, label]) => (
                        <button key={code} type="button" onClick={() => toggleDay(code)}
                          className={`px-2 py-1 rounded-md border text-xs ${rec.byday.includes(code) ? 'bg-primary text-primary-foreground border-primary' : 'border-border/60'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                  {rec.freq === 'monthly' && (
                    <div className="flex items-center gap-2 text-sm">
                      <span>Day of month</span>
                      <Input type="number" min="1" max="31" value={rec.bymonthday} onChange={(e) => setR('bymonthday', e.target.value)} className="w-20" placeholder="15" />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>Ends:</span>
                    <label className="flex items-center gap-1"><input type="radio" name="endtype" checked={rec.endType === 'count'} onChange={() => setR('endType', 'count')} /> After</label>
                    <Input type="number" min="1" max="100" value={rec.endCount} onChange={(e) => setR('endCount', e.target.value)} className="w-20" disabled={rec.endType !== 'count'} />
                    <span>occurrences</span>
                    <label className="flex items-center gap-1 ml-2"><input type="radio" name="endtype" checked={rec.endType === 'until'} onChange={() => setR('endType', 'until')} /> On date</label>
                    <Input type="date" value={rec.endDate} onChange={(e) => setR('endDate', e.target.value)} className="w-44" disabled={rec.endType !== 'until'} />
                  </div>
                  <p className="text-xs opacity-60">Safety cap: up to 1 year ahead or 100 occurrences, whichever comes first.</p>
                </div>
              )}
            </div>
          )}

          {/* Posters */}
          <div className="rounded-lg border border-border/60 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /><span className="text-sm font-medium">Posters (1–3) *</span></div>
              <label className={`inline-flex items-center gap-1 text-sm cursor-pointer ${posters.length >= 3 ? 'opacity-40 pointer-events-none' : ''}`}>
                <Upload className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Add poster'}
                <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" onChange={onPickPoster} disabled={uploading || posters.length >= 3} />
              </label>
            </div>
            <p className="text-xs opacity-60">JPG, PNG or WebP, max 5 MB each. The first poster is the card image.</p>
            <div className="grid grid-cols-3 gap-3">
              {posters.map((p, i) => (
                <div key={p.key} className="relative rounded-md overflow-hidden border border-border/60">
                  <img src={p.url} alt={`Poster ${i + 1}`} className="w-full h-28 object-cover" />
                  {i === 0 && <span className="absolute top-1 left-1 text-[10px] bg-primary text-primary-foreground px-1 rounded">Cover</span>}
                  <div className="absolute bottom-1 right-1 flex gap-1">
                    {i > 0 && <button type="button" onClick={() => movePoster(i, -1)} className="bg-background/80 rounded p-0.5" title="Move left"><ArrowUp className="h-3 w-3 rotate-[-90deg]" /></button>}
                    {i < posters.length - 1 && <button type="button" onClick={() => movePoster(i, 1)} className="bg-background/80 rounded p-0.5" title="Move right"><ArrowDown className="h-3 w-3 rotate-[-90deg]" /></button>}
                    <button type="button" onClick={() => removePoster(i)} className="bg-background/80 rounded p-0.5 text-red-500" title="Remove"><X className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
              {posters.length === 0 && <p className="col-span-3 text-xs opacity-50 py-4 text-center">No posters yet.</p>}
            </div>
          </div>

          {/* Performers */}
          <div className="rounded-lg border border-border/60 p-4 space-y-3">
            <div className="flex items-center gap-2"><UserPlus className="h-4 w-4" /><span className="text-sm font-medium">Performers</span></div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.performing_self} onCheckedChange={(v) => set('performing_self', v)} />
              I am performing at this event
            </label>
            <div>
              <Label className="text-xs">Add an approved DJ</Label>
              <select value="" onChange={(e) => { if (e.target.value) addDjId(e.target.value); }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select a DJ…</option>
                {approvedDjs.map((d) => <option key={d.id} value={d.id}>{d.dj_name}{d.city ? ` — ${d.city}` : ''}</option>)}
              </select>
              <div className="flex flex-wrap gap-2 mt-2">
                {perfDjIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs">
                    {djName(id)}
                    <button type="button" className="opacity-70 hover:opacity-100" onClick={() => setPerfDjIds((l) => l.filter((x) => x !== id))}>×</button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Add an external performer (name)</Label>
              <div className="flex gap-2">
                <Input value={guestInput} onChange={(e) => setGuestInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }} placeholder="e.g. MC Spotlight" maxLength={100} />
                <Button type="button" variant="outline" onClick={addGuest}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {perfGuests.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs">
                    {g}
                    <button type="button" className="opacity-70 hover:opacity-100" onClick={() => setPerfGuests((l) => l.filter((x) => x !== g))}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {isOccurrence ? (
              <Button type="button" disabled={busy} onClick={onSaveOccurrence}><Save className="h-4 w-4 mr-2" />{busy ? 'Saving…' : 'Save occurrence'}</Button>
            ) : (
              <>
                <Button type="button" variant="outline" disabled={busy} onClick={onSaveDraft}><Save className="h-4 w-4 mr-2" />Save Draft</Button>
                <Button type="button" disabled={busy} onClick={onPublish}><Send className="h-4 w-4 mr-2" />{busy ? 'Working…' : 'Publish Now'}</Button>
              </>
            )}
            <Button type="button" variant="ghost" onClick={onCancel}><X className="h-4 w-4 mr-2" /> Cancel</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
